import { z } from 'zod';
import { IsoDate, IsoDateTime, Uuid } from './common.js';

/**
 * Carte des clients (lot E1, CDC §3.7 `GET /api/map/clients`) et géocodage des adresses Axonaut
 * (Réglages, administrateur). Logique pure associée : `packages/shared/src/map.ts`.
 */

// ------------------------------------------------------------------ Carte

/**
 * Statut d'un client sur la carte (règles dans `mapClientStatus`) :
 * - `subscribed` : abonnement `active`, `trialing` ou `past_due` ;
 * - `cancelled` : abonnement résilié ou `clients.cancelled_on` renseigné ;
 * - `no_subscription` : devis accepté, site, facture ou date de démarrage, sans abonnement ;
 * - `prospect` : seulement des devis en attente.
 */
export const MapClientStatus = z.enum(['subscribed', 'no_subscription', 'cancelled', 'prospect']);
export type MapClientStatus = z.infer<typeof MapClientStatus>;

export const MapClient = z.object({
  id: Uuid,
  name: z.string(),
  /** Commune renvoyée par le géocodage (ou lue dans l'adresse à défaut). */
  city: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  status: MapClientStatus,
  /** Formule de l'abonnement (abonné), ou de l'abonnement résilié ; null sinon. */
  plan: z.string().nullable(),
  /** Client depuis : date de démarrage, de mise en ligne ou du premier devis accepté (prospect : premier devis). */
  since: IsoDate.nullable(),
  /** Département 85, d'après le code postal du géocodage (ou de l'adresse). */
  inVendee: z.boolean(),
});
export type MapClient = z.infer<typeof MapClient>;

export const MapCityCount = z.object({ city: z.string(), count: z.int().min(1) });
export type MapCityCount = z.infer<typeof MapCityCount>;

/** Répartition des clients placés sur la carte, prospects exclus (comme la maquette). */
export const MapClientsSummary = z.object({
  /** Clients placés, hors prospects. */
  total: z.int().min(0),
  inVendee: z.int().min(0),
  outsideVendee: z.int().min(0),
  /** Prospects placés (anneaux sur la carte), comptés à part. */
  prospects: z.int().min(0),
  /** Communes les plus représentées (hors prospects), 10 au plus, de la plus fréquente à la moins fréquente. */
  topCities: z.array(MapCityCount),
});
export type MapClientsSummary = z.infer<typeof MapClientsSummary>;

export const MapClientsResponse = z.object({
  items: z.array(MapClient),
  summary: MapClientsSummary,
});
export type MapClientsResponse = z.infer<typeof MapClientsResponse>;

// ------------------------------------------------------------------ Géocodage (Réglages)

export const GeocodeStatus = z.enum(['OK', 'NOT_FOUND', 'ERROR']);
export type GeocodeStatus = z.infer<typeof GeocodeStatus>;

export const GeocodingIssue = z.object({
  id: Uuid,
  name: z.string(),
  address: z.string(),
  geocodedAt: IsoDateTime.nullable(),
});
export type GeocodingIssue = z.infer<typeof GeocodingIssue>;

/** `GET /api/admin/geocoding` : avancement du géocodage des fiches clients. */
export const GeocodingOverview = z.object({
  /** `fake` : coordonnées des communes de la maquette (démonstration) ; `real` : Géoplateforme (BAN). */
  mode: z.enum(['fake', 'real']),
  counts: z.object({
    total: z.int().min(0),
    ok: z.int().min(0),
    notFound: z.int().min(0),
    error: z.int().min(0),
    /** Adresse renseignée, pas encore géocodée (ou modifiée depuis). */
    pending: z.int().min(0),
    noAddress: z.int().min(0),
  }),
  /** Adresses introuvables (à corriger dans Axonaut), par nom. */
  notFound: z.array(GeocodingIssue),
  lastGeocodedAt: IsoDateTime.nullable(),
});
export type GeocodingOverview = z.infer<typeof GeocodingOverview>;

/** `POST /api/admin/geocoding/run` : tâche `geocode:clients` mise en file. */
export const GeocodingRunQueued = z.object({
  queued: z.literal(true),
  queue: z.string(),
  jobId: z.string(),
});
export type GeocodingRunQueued = z.infer<typeof GeocodingRunQueued>;
